/**
 * response.js — Command River shell (frameless full-bleed blade).
 * Soft≠Live Page39 / Page219 · nav_plane ∥ act_plane.
 * Map = spatial send · Cameras = Live lenses · Response = human+AI command river.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, Ico, Alert, SOFT, stay, _stop, useEsc, hashSearchParams, handoffKnownParamsOf } from "./ui.js";
import { path, useApi } from "./api.js";
import { isLocalHost } from "./pathseal.js";
import { softResponseErrorOf } from "./response-live.js";
import { LIVE_SYNC_HONESTY, SOFT_SYNC_HONESTY } from "./response-soft-demo.js";
import {
  PRIMARY_TABS,
  CONTEXT_TOOLS,
  RESPONSE_ROUTES,
  TAB_RESIDUAL,
  MODULE_CATALOG,
} from "./response-registry.js";
import { BoardPane, responseBoardSeatOf } from "./response-board.js";
import { DispatchArc } from "./response-gyro.js";
import { Assign, responseAssignSeatOf, assignDevicesWithFixOf, assignRegisterIdsOf, assignHandoffPickOf } from "./response-assign.js";
import { Roster, responseRosterSeatOf, rosterRowsClampOf } from "./response-roster.js";
import { Cell } from "./response-cell.js";
import {
  CasesPane,
  RobotsPane,
} from "./response-legacy-panes.js";

export { softResponseErrorOf };
export { responseBoardSeatOf };
export { responseAssignSeatOf, assignDevicesWithFixOf, assignRegisterIdsOf, assignHandoffPickOf };
export { responseRosterSeatOf, rosterRowsClampOf };
export { Assign, Roster };
export { PRIMARY_TABS, CONTEXT_TOOLS, MODULE_CATALOG };

const POSTURE_DWELL_MS = 12000;
/* prepare/equipment/rules alias away Soft≠ invent Response duplicate desks; robots stay Live */
const LEGACY_IDS = new Set(["robots"]);

function safeResponseShellPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_status") return "/response/status";
    return "";
  }
}

export function responseShellSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const tabMax = Number(lim.tab_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    navAllowed: p.nav_plane_allowed === true,
    actAllowed: p.act_plane_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    tabPaintMax:
      Number.isFinite(tabMax) && tabMax > 0 ? Math.floor(tabMax) : 16,
  };
}

const PANES = {
  board: BoardPane,
  assign: Assign,
  roster: Roster,
  cell: Cell,
  cases: CasesPane,
  robots: RobotsPane,
};

function catalogOf(id) {
  return MODULE_CATALOG.find((row) => row.id === id) || null;
}

/**
 * Command River rail — frameless left blade (≠ Cameras Live, ≠ boxed chrome).
 */
function RiverChrome({
  modeLabel,
  liveOn,
  activeId,
  seat,
  setTab,
  openTool,
  reReadShell,
  arc,
  hold,
  setHold,
  setArc,
  toolAt,
}) {
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const hh = String(clock.getHours()).padStart(2, "0");
  const mm = String(clock.getMinutes()).padStart(2, "0");
  const ss = String(clock.getSeconds()).padStart(2, "0");
  const collapsed = arc && !hold;

  return html`
    <aside className="rr-rail" role="banner" data-cmc-rsp="rsp-2"
      data-arc=${arc ? "1" : "0"} data-hold=${hold ? "1" : "0"}
      data-collapsed=${collapsed ? "1" : "0"}
      aria-label="Command River arc drawer"
      onMouseEnter=${() => setHold && setHold(true)}
      onMouseLeave=${() => setHold && setHold(false)}
      onFocusCapture=${() => setHold && setHold(true)}
      onBlurCapture=${(e) => {
        if (!setHold) return;
        const next = e.relatedTarget;
        if (next && e.currentTarget.contains(next)) return;
        setHold(false);
      }}>
      <div className="rr-rail__bow" aria-hidden="true"/>
      <div className="rr-rail__edge" aria-hidden="true"/>
      <button type="button" className="rr-rail__grip" aria-label=${collapsed ? "Expand menu" : "Collapse menu"}
        aria-expanded=${collapsed ? "false" : "true"}
        title=${collapsed ? "Show menu" : "Hide menu"}
        onClick=${() => {
          if (!setArc || !setHold) return;
          if (collapsed) {
            setArc(false);
            setHold(false);
            return;
          }
          setArc(true);
          setHold(false);
        }}>
        <i/><i/><i/>
      </button>

      <header className="rr-rail__head">
        <strong className="rr-rail__mark">${collapsed ? "R" : "Response"}</strong>
        <span className="rr-rail__posture" data-live=${liveOn ? "1" : "0"}>
          <i aria-hidden="true"/> ${modeLabel}
        </span>
        ${seat.role ? html`<em className="rr-rail__role">${seat.role}</em>` : null}
      </header>

      <div className="rr-rail__watch" aria-label="Local watch">
        <b>${hh}<span>:</span>${mm}</b>
        <small>${ss}</small>
      </div>

      <nav className="rr-rail__modes" role="tablist" aria-label="Command River modes">
        <span className="rr-rail__sect">Modes</span>
        ${PRIMARY_TABS.map((t, i) => html`
          <button key=${t.id} type="button" role="tab"
            aria-selected=${activeId === t.id ? "true" : "false"}
            data-on=${activeId === t.id ? "1" : "0"}
            data-mode=${t.id}
            disabled=${!seat.navAllowed}
            title=${t.label + " — " + t.say}
            onClick=${() => setTab(t.id)}>
            <span className="rr-rail__glyph" aria-hidden="true">${t.glyph || String(i + 1)}</span>
            <span className="rr-rail__idx">${String(i + 1).padStart(2, "0")}</span>
            <span className="rr-rail__lab">
              <b>${t.label}</b>
              <small>${t.say}</small>
            </span>
          </button>`)}
      </nav>

      <div className="rr-rail__quay" aria-label="Response tools">
        <span className="rr-rail__sect">Tools</span>
        ${CONTEXT_TOOLS.map((t) => html`
          <button type="button" className="rr-rail__tool" key=${t.id}
            data-on=${toolAt === t.id ? "1" : "0"}
            data-tool=${t.id}
            disabled=${!seat.navAllowed} onClick=${() => openTool(t.id)} title=${t.label}>
            <${Ico} d=${t.icon} w=${15}/>
            <span>${t.label}</span>
          </button>`)}
        <button type="button" className="rr-rail__tool rr-rail__tool--refresh"
          disabled=${!seat.reReadAllowed}
          title="Refresh Command River"
          onClick=${() => reReadShell && reReadShell()}>
          <${Ico} d="refresh" w=${15}/>
          <span>Refresh</span>
        </button>
      </div>
    </aside>
  `;
}

function ToolDock({ toolAt, seat, toast, onClose }) {
  const ToolPane = PANES[toolAt];
  if (!ToolPane) return null;
  const label = CONTEXT_TOOLS.find((t) => t.id === toolAt)?.label || "Response tool";
  return html`
    <${M.aside} className="rr-tool-float" role="dialog" aria-modal="false"
      aria-label=${label}
      initial=${{ opacity: 0, y: 12, scale: 0.98 }} animate=${{ opacity: 1, y: 0, scale: 1 }}
      exit=${{ opacity: 0, y: 8, scale: 0.98 }} transition=${SOFT}>
      <header className="rr-tool-float__head">
        <div>
          <span>Floating tool · main Queue unchanged</span>
          <strong>${label}</strong>
        </div>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${onClose}
          aria-label="Close response tool">
          <${Ico} d="close" w=${14}/> Close
        </button>
      </header>
      <div className="rr-tool-float__body">
        <${ToolPane} toast=${toast} shellSeat=${seat}/>
      </div>
    <//>
  `;
}

export default function Response({ sub, toast }) {
  const routeAt = RESPONSE_ROUTES.includes(sub) ? sub : "board";
  const routeTool = CONTEXT_TOOLS.some((t) => t.id === routeAt) ? routeAt : "";
  const isLegacy = LEGACY_IDS.has(routeAt);
  const routeCore = PRIMARY_TABS.some((t) => t.id === routeAt)
    ? routeAt
    : (isLegacy ? routeAt : "board");
  const [coreAt, setCoreAt] = useState(routeCore === "cases" ? "cases" : (isLegacy ? routeAt : "board"));
  const [toolAt, setToolAt] = useState(routeTool);

  useEffect(() => {
    if (routeTool) {
      setToolAt(routeTool);
      return;
    }
    setToolAt("");
    if (isLegacy) setCoreAt(routeAt);
    else if (routeAt === "cases") setCoreAt("cases");
    else setCoreAt("board");
  }, [routeAt, routeTool, isLegacy]);

  const posture = useApi(safeResponseShellPath("get__response_status"));
  const seat = responseShellSeatOf(posture.loading ? null : (posture.data || {}));
  const [postureStuck, setPostureStuck] = useState(false);
  useEffect(() => {
    if (!posture.loading) {
      setPostureStuck(false);
      return undefined;
    }
    const id = setTimeout(() => setPostureStuck(true), POSTURE_DWELL_MS);
    return () => clearTimeout(id);
  }, [posture.loading]);

  const tabResidual = TAB_RESIDUAL[toolAt || coreAt] || "page219-response";
  const handoff = handoffKnownParamsOf(hashSearchParams());
  const returnTo = ["/response/board", "/response/cases"].includes(handoff.return_to)
    ? handoff.return_to
    : "/response/board";

  const wrapRef = useRef(null);
  const [arc, setArc] = useState(false);
  const [hold, setHold] = useState(false);
  /* Arc drawer is grip-only — auto-collapse on scroll made the desk feel broken. */

  const reReadShell = () => {
    if (!seat.reReadAllowed && !postureStuck) {
      toast && toast("Refresh is not available right now", "warn");
      return;
    }
    setPostureStuck(false);
    posture.reload && posture.reload();
    toast && toast("Command River refreshed", "go");
  };

  const setTab = (id) => {
    if (!seat.navAllowed) {
      toast && toast("Response navigation is sealed for this account", "warn");
      return;
    }
    setToolAt("");
    setCoreAt(id);
    stay("/response/" + id);
  };

  const openTool = (id) => {
    if (!seat.navAllowed) {
      toast && toast("Response navigation is sealed for this account", "warn");
      return;
    }
    setToolAt(id);
    stay(`/response/${id}?return_to=${encodeURIComponent(returnTo)}`);
  };

  const closeTool = useCallback(() => {
    if (!toolAt) return;
    setToolAt("");
    stay(returnTo);
  }, [toolAt, returnTo]);
  useEsc(closeTool);

  const LegacyPane = isLegacy && !toolAt ? PANES[coreAt] : null;
  const cat = catalogOf(isLegacy ? coreAt : "");
  const modeLabel = posture.loading
    ? (postureStuck ? "Timed out" : "…")
    : (seat.navAllowed ? "Live" : "Sealed");
  const activeId = isLegacy ? "" : (coreAt === "cases" ? "cases" : "board");

  if (!posture.loading && !seat.navAllowed && !seat.actAllowed) {
    return html`
      <div className="sheet resp-desk resp-desk--liquid desk-craft" data-cmc-response="page39"
        data-cmc-page="page219-response" data-cmc-residual="page219-response" data-cmc-rsp="rsp-1">
        <div className="wrap">
          <${Alert} tone="bad" title="Response workspace sealed"
            say="Your account cannot open Response yet. Sign in with a seat that can view dispatches, or ask a manager."/>
          ${seat.detail ? html`<p className="note">${softResponseErrorOf(seat.detail)}</p>` : null}
          <div className="btn-row" style=${{ marginTop: 12 }}>
            <button type="button" className="btn btn--ghost btn--sm"
              disabled=${!seat.reReadAllowed || posture.loading} onClick=${reReadShell}>
              <${Ico} d="refresh" w=${14}/> Refresh
            </button>
          </div>
        </div>
      </div>`;
  }

  return html`
    <div className="sheet resp-desk resp-desk--liquid desk-craft resp-desk--river"
      data-cmc-response="page39"
      data-cmc-page="page219-response"
      data-cmc-tab-residual=${tabResidual}
      data-cmc-residual=${"page219-response"}
      data-cmc-rsp="rsp-1"
      data-cmc-aiw="aiw-4"
      data-resp-craft="blade-1"
      data-flat="1">
      <div className="wrap wrap--river" ref=${wrapRef}
        data-arc=${arc ? "1" : "0"} data-hold=${hold ? "1" : "0"}
        data-tool=${toolAt ? "1" : "0"}>
        <${RiverChrome}
          modeLabel=${modeLabel}
          liveOn=${seat.navAllowed}
          activeId=${activeId}
          seat=${seat}
          setTab=${setTab}
          openTool=${openTool}
          reReadShell=${reReadShell}
          arc=${arc}
          hold=${hold}
          setHold=${setHold}
          setArc=${setArc}
          toolAt=${toolAt}
        />

        <div className="rr-stage">
          <p className="rr-soft-note">${isLocalHost() ? SOFT_SYNC_HONESTY : LIVE_SYNC_HONESTY}</p>
          ${!posture.loading && !seat.actAllowed ? html`<${Alert} tone="info" title="Writes sealed for this account"
            say=${softResponseErrorOf(seat.detail || "You can read some queues, but create or change actions need a higher seat.")}/>` : null}
          ${posture.loading && postureStuck ? html`<${Alert} tone="warn" title="Access check timed out"
            say="Response posture did not answer in time. Modes stay sealed — Refresh to retry."/>` : null}

          ${seat.navAllowed ? html`
            <${AnimatePresence} mode="wait">
              ${LegacyPane ? html`
                <${M.div} key=${"legacy-" + coreAt} className="resp-legacy"
                  initial=${{ opacity: 0, y: 10 }} animate=${{ opacity: 1, y: 0 }} exit=${{ opacity: 0 }}
                  transition=${{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}>
                  <${Alert} tone="warn" title="Extracted module · pending review"
                    say=${`This pane was pulled out of Response for triage (${cat ? cat.suggestedHome : "review"}). Live state is never invented here.`}/>
                  <${LegacyPane} toast=${toast} shellSeat=${seat}/>
                <//>
              ` : coreAt === "cases" ? html`
                <${M.div} key="mission" className="rr-pane rr-pane--mission rr-mission"
                  initial=${{ opacity: 0 }} animate=${{ opacity: 1 }} exit=${{ opacity: 0 }}
                  transition=${{ duration: 0.16, ease: [0.2, 0, 0.2, 1] }}>
                  <${CasesPane} toast=${toast} shellSeat=${seat}/>
                <//>
              ` : html`
                <${M.div} key="command" className="rr-pane rr-pane--command"
                  initial=${{ opacity: 0 }} animate=${{ opacity: 1 }} exit=${{ opacity: 0 }}
                  transition=${{ duration: 0.16, ease: [0.2, 0, 0.2, 1] }}>
                  <${DispatchArc}
                    toast=${toast}
                    shellSeat=${seat}
                    onOpenCase=${(incidentId) => stay("/response/cases?incident=" + encodeURIComponent(incidentId))}
                  />
                <//>
              `}
            <//>
          ` : html`
            <${Alert} tone="warn" title="Navigation sealed"
              say=${posture.loading
                ? (postureStuck
                  ? "Access check timed out — Refresh to retry."
                  : "Checking your Response access…")
                : "Sign in with a Response seat or ask a manager to open this workspace."}/>
          `}
        </div>

        <${AnimatePresence}>
          ${toolAt ? html`<${ToolDock} key=${toolAt} toolAt=${toolAt} seat=${seat} toast=${toast} onClose=${closeTool}/>` : null}
        <//>
      </div>
    </div>`;
}
